import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Slider, Typography, Card, Space, Tag } from 'antd';
import { ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

/**
 * SolidNetWidget (Flagship Implementation)
 * * Upgrades:
 * - Ant Design components for consistent branding
 * - Introductory explanation support
 * - Perspective-correct 3D folding animation
 * - Active face-mapping (click to highlight)
 */
export const SolidNetWidget = ({ config, content }) => {
    // --- Safety & Config ---
    const safeNum = (v, fallback) => (Number.isFinite(Number(v)) ? Number(v) : fallback);
    const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

    const cfg = config || {};
    const explanation = content?.explanation || "Slide to see how this rectangular prism unfolds into a 2D net.";

    const w0 = safeNum(cfg.width, 4);
    const h0 = safeNum(cfg.height, 3);
    const d0 = safeNum(cfg.depth, 2);

    const width = clamp(w0, 0.5, 10);
    const height = clamp(h0, 0.5, 10);
    const depth = clamp(d0, 0.5, 10);

    // --- State ---
    const [unfold, setUnfold] = useState(clamp(safeNum(cfg.unfoldPercent, 70), 0, 100));
    const [autoPlay, setAutoPlay] = useState(false);
    const [rot, setRot] = useState({ x: -25, y: 35 });
    const [highlightFace, setHighlightFace] = useState(null);

    const dragRef = useRef({ dragging: false, px: 0, py: 0, rx: 0, ry: 0 });
    const stageRef = useRef(null);
    const [stageSize, setStageSize] = useState({ w: 640, h: 420 });

    // --- Effects ---
    useEffect(() => {
        const el = stageRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => {
            const r = entries[0]?.contentRect;
            if (r) setStageSize({ w: r.width, h: r.height });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (!autoPlay) return;
        const t = window.setInterval(() => {
            setUnfold(u => {
                if (u >= 100) { setAutoPlay(false); return 100; }
                return u + 2;
            });
        }, 30);
        return () => window.clearInterval(t);
    }, [autoPlay]);

    // --- Math ---
    const faces = useMemo(() => {
        const sa = 2 * (width * height + width * depth + height * depth);
        return {
            front: { w: width, h: height, label: "A" },
            back: { w: width, h: height, label: "F" },
            top: { w: width, h: depth, label: "B" },
            bottom: { w: width, h: depth, label: "D" },
            left: { w: depth, h: height, label: "E" },
            right: { w: depth, h: height, label: "C" },
            surfaceArea: sa
        };
    }, [width, height, depth]);

    const t = unfold / 100;
    const pxPerUnit = useMemo(() => {
        const spanX = width + (depth * 2);
        const spanY = height + (depth * 2);
        return clamp(Math.min((stageSize.w - 120) / spanX, (stageSize.h - 120) / spanY), 20, 55);
    }, [stageSize, width, height, depth]);

    const reset = () => {
        setUnfold(clamp(safeNum(cfg.unfoldPercent, 70), 0, 100));
        setRot({ x: -25, y: 35 });
        setHighlightFace(null);
        setAutoPlay(false);
    };

    const renderFace = (key, x, y, origin, rotation) => {
        const isHot = highlightFace === key;
        const rx = rotation?.x ? rotation.x * t : 0;
        const ry = rotation?.y ? rotation.y * t : 0;

        return (
            <div
                key={key}
                className={`ch-solidnet-face ${isHot ? 'is-highlighted' : ''}`}
                onClick={() => setHighlightFace(prev => prev === key ? null : key)}
                style={{
                    left: (stageSize.w / 2) + (x * pxPerUnit) - (faces[key].w * pxPerUnit / 2),
                    top: (stageSize.h / 2) + (y * pxPerUnit) - (faces[key].h * pxPerUnit / 2),
                    width: faces[key].w * pxPerUnit,
                    height: faces[key].h * pxPerUnit,
                    transformStyle: "preserve-3d",
                    transformOrigin: origin,
                    transform: `rotateX(${rx}deg) rotateY(${ry}deg) translateZ(${(t * depth * pxPerUnit) / 2}px)`,
                    transition: autoPlay ? "none" : "transform 0.1s linear, background-color 0.3s"
                }}
            >
                <Tag className="ch-solidnet-face-label">{faces[key].label}</Tag>
            </div>
        );
    };

    return (
        <div className="ch-solidnet-widget animate-slide-up">
            <Space orientation="vertical" style={{ width: '100%' }} size="middle">

                {/* Header */}
                <div className="flexSpaceBetween flexAlignCentre">
                    <Title level={4} style={{ margin: 0 }}>3D Solid Net</Title>
                    <Button
                        icon={<ReloadOutlined />}
                        size="small"
                        onClick={reset}
                    >
                        Reset
                    </Button>
                </div>

                {/* Intro Sentence */}
                <Paragraph type="secondary" italic style={{ marginBottom: 0 }}>
                    {explanation}
                </Paragraph>

                {/* 3D Stage */}
                <div
                    ref={stageRef}
                    className="ch-solidnet-stage"
                    onPointerDown={e => {
                        dragRef.current = { dragging: true, px: e.clientX, py: e.clientY, rx: rot.x, ry: rot.y };
                        e.currentTarget.setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={e => {
                        if (!dragRef.current.dragging) return;
                        const dx = e.clientX - dragRef.current.px;
                        const dy = e.clientY - dragRef.current.py;
                        setRot({ x: clamp(dragRef.current.rx - dy * 0.3, -85, 85), y: dragRef.current.ry + dx * 0.3 });
                    }}
                    onPointerUp={e => { dragRef.current.dragging = false; e.currentTarget.releasePointerCapture(e.pointerId); }}
                >
                    <div style={{
                        position: "absolute", width: "100%", height: "100%",
                        transformStyle: "preserve-3d",
                        transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`
                    }}>
                        {renderFace("front", 0, 0, "center center")}
                        {renderFace("top", 0, -(height + depth)/2, "50% 100%", { x: -90 })}
                        {renderFace("bottom", 0, (height + depth)/2, "50% 0%", { x: 90 })}
                        {renderFace("left", -(width + depth)/2, 0, "100% 50%", { y: 90 })}
                        {renderFace("right", (width + depth)/2, 0, "0% 50%", { y: -90 })}
                        {renderFace("back", 0, -(height + depth)/2 - depth, "50% 100%", { x: -180 })}
                    </div>

                    <Card className="ch-solidnet-sa-card" size="small">
                        <Text strong type="success" style={{ fontSize: '10px' }}>SURFACE AREA</Text>
                        <div className="flexSpaceBetween">
                            <Text>Total Area:</Text>
                            <Text strong>{faces.surfaceArea.toFixed(1)} u²</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: 4 }}>
                            SA = 2(wh + wd + hd)
                        </Text>
                    </Card>

                    <div className="ch-solidnet-stage-hint" style={{ position: 'absolute', bottom: 8, left: 16, fontSize: 11, opacity: 0.6 }}>
                        Drag to rotate solid
                    </div>
                </div>

                {/* Controls */}
                <div className="ch-solidnet-controls-panel">
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space size="middle" style={{ flex: 1 }}>
                            <Text strong>Net</Text>
                            <Slider
                                min={0}
                                max={100}
                                value={unfold}
                                onChange={setUnfold}
                                style={{ width: 240 }}
                                tooltip={{ open: false }}
                            />
                            <Text strong>3D</Text>
                        </Space>

                        <Button
                            type={autoPlay ? "default" : "primary"}
                            icon={autoPlay ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                            onClick={() => { if(unfold >= 100) setUnfold(0); setAutoPlay(!autoPlay); }}
                        >
                            {autoPlay ? "Pause" : (unfold >= 100 ? "Replay" : "Fold")}
                        </Button>
                    </Space>
                </div>
            </Space>
        </div>
    );
};